import React, { useState, useMemo } from "react";
import cx from "classnames";
import {
  startOfToday,
  addDays,
  subDays,
  isYesterday,
  isToday,
  isTomorrow,
  isSameDay,
  nextSaturday,
  nextSunday,
  format,
  startOfDay,
} from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/Popover";
import { Calendar } from "@/components/Calendar";
import { useIsMobile } from "@/hooks/useDisplay";
import { navigate } from "@/utilities/navigate";
import { ReactComponent as IconDate } from "assets/icons/icon-date.svg";
import { getDateRangeForTabName } from "@/features/racing/utils/dates";

import * as styles from "./DateSelector.module.scss";

const mapDateToUrl = (date: Date) => {
  if (isYesterday(date)) {
    return `yesterday`;
  }

  if (isToday(date)) {
    return `today`;
  }

  if (isTomorrow(date)) {
    return `tomorrow`;
  }

  if (isSameDay(date, nextSaturday(startOfToday()))) {
    return `saturday`;
  }

  if (isSameDay(date, nextSunday(startOfToday()))) {
    return `sunday`;
  }

  return format(startOfDay(date), "yyyy-MM-dd");
};

type DateSelectorProps = {
  selectedTab?: string;
};

const DateSelector = ({ selectedTab }: DateSelectorProps) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  const minDate = useMemo(() => addDays(startOfToday(), 5), []);
  const maxDate = useMemo(() => subDays(startOfToday(), 10), []);

  const [currentDate] = getDateRangeForTabName(
    selectedTab?.replace("/", "") || "",
  );

  return (
    <div
      className={cx(styles.container, {
        [styles.isOpen]: isOpen,
      })}
    >
      <Popover open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
        <PopoverTrigger className={styles.trigger}>
          <IconDate />
        </PopoverTrigger>
        <PopoverContent
          align={`end`}
          alignOffset={isMobile ? 8 : -6}
          className={styles.popover}
          sideOffset={-2}
        >
          <Calendar
            mode="single"
            selected={currentDate}
            disabled={{
              before: maxDate,
              after: minDate,
            }}
            onSelect={(date) => {
              setIsOpen(false);
              if (!date) return;
              navigate(`/racing/betting/${mapDateToUrl(date)}`);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { DateSelector };
